import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { parseEntities } from 'parse-entities';
import { nanoid } from 'nanoid';
import classNames from 'classnames';

import Chevron from 'icons/chevron.svg';

import { AccordionItemContent } from './AccordionItemContent';

interface Props {
  title: string;
  isOpen: boolean;
  onClick: () => void;
  content: React.ReactElement;
  updateMaxHeight: (a: number) => void;
  updateOpenedHeight: (a: number) => void;
  lesserIndex: boolean;
}

let scrollPos = 0;
let scrollUp: boolean = 0;
const tabletScrollOffset = -560;
const laptopWidth = 1440;
const desktopWidth = 1920;

if (typeof window !== 'undefined') {
  window.addEventListener('scroll', () => {
    scrollUp = document.body.getBoundingClientRect().top > scrollPos;
    scrollPos = document.body.getBoundingClientRect().top;
  });
}

function handleScrollUp(offset: number, name: React.RefObject<HTMLDivElement>) {
  const yOffset = offset;
  const y = name.current!.getBoundingClientRect().top + window.pageYOffset + yOffset;

  window.scrollTo({ top: y, behavior: 'smooth' });
}

function handleButtonClick(name: React.RefObject<HTMLDivElement>, isLess: boolean) {
  if (typeof window !== 'undefined') {
    if (name && window.innerWidth === desktopWidth) {
      name.current!.scrollIntoView({ block: 'center', behavior: 'smooth', inline: 'nearest' });
    } else if (name && window.innerWidth <= laptopWidth) {
      if (isLess) {
        name.current!.scrollIntoView({ block: 'start', behavior: 'smooth' });
      } else {
        handleScrollUp(tabletScrollOffset, name);
      }
    }
  }
}

export const AccordionItem = ({
  title,
  isOpen,
  onClick,
  content,
  updateMaxHeight,
  updateOpenedHeight,
  lesserIndex,
}: Props) => {
  const articleRef = useRef<null | HTMLDivElement>(null);
  const titleRef = useRef<HTMLAnchorElement>(null);

  const [titleWidth, setTitleWidth] = useState(0);
  const [windowWidth, setWindowWidth] = useState(0);

  const handleResize = (innerWidth: number) => {
    setWindowWidth(innerWidth);
  };

  useLayoutEffect(() => {
    if (titleRef.current) {
      setTitleWidth(titleRef.current.offsetWidth);
    }
  });

  useEffect(() => {
    window.addEventListener('resize', () => handleResize(window.innerWidth));

    return () => {
      window.removeEventListener('resize', () => handleResize(window.innerWidth));
    };
  }, [titleWidth, windowWidth, titleRef.current?.offsetWidth]);

  return (
    <div key={nanoid()} className={classNames('accordion-item', { opened: isOpen })} ref={articleRef}>
      <div
        className="accordion-title"
        ref={articleRef}
        onClick={() => {
          onClick();
          handleButtonClick(articleRef, lesserIndex);
        }}
      >
        <h3 className="body-2">{parseEntities(title)}</h3>
        <div className="accordion-icon">
          <Chevron />
        </div>
      </div>

      <AccordionItemContent
        key={nanoid()}
        content={content}
        isOpen={isOpen}
        updateMaxHeight={updateMaxHeight}
        updateOpenedHeight={updateOpenedHeight}
      />
    </div>
  );
};
